import * as ex from 'excalibur';
import { TILE_SIZE, GRID_WIDTH, GRID_HEIGHT } from './config/constants';
import { Allegiance, ControlType } from './types/enums';
import { Actor } from './core/actor';
import { Unit } from './core/unit';
import { Grid } from './core/grid';
import { TurnManager } from './core/turnManager';
import { UnitFactory } from './factories/unitFactory';
import { EncounterGenerator } from './factories/encounterGenerator';

export class Game extends ex.Scene {
  // Game elements
  public grid: Grid;
  private turnManager: TurnManager | null = null;
  private _currentActor: Actor | null = null;
  private gameRunning: boolean = false;
  private useRandomEncounter: boolean = true;
  
  // UI elements
  private turnText: ex.Label;
  private healthText: ex.Label;
  private actionLog: ex.Label[] = [];
  private actionLogData: string[] = [];

  constructor() {
    super();
  }

  // Get the current actor whose turn it is
  get currentActor(): Actor | null {
    return this._currentActor;
  }

  // Initialization
  public onInitialize() {
    // Create the grid
    this.grid = new Grid(this);
    
    // Set up a random grid pattern with walls
    const gridPattern = this.grid.generateRandomPattern(0.15);
    this.grid.initialize(gridPattern);
    
    // Create UI for displaying turn information
    this.createUI();
    
    // Create units based on the scenario
    this.createUnits();
    
    // Start game loop
    this.gameRunning = true;
    this.processNextAction();
  }

  // Called when a round is completed (all units have taken a turn)
  private onRoundComplete = () => {
    console.log(`%c===== COMPLETED ROUND =====`, 'font-weight: bold; font-size: 14px; color: green;');
    
    // List remaining units for both sides
    const playerUnits = this.grid.getActorsByAllegiance(Allegiance.Player);
    const enemyUnits = this.grid.getActorsByAllegiance(Allegiance.Enemy);
    
    console.log(`%cRemaining Player units (${playerUnits.length}):`, 'font-weight: bold; color: blue;');
    playerUnits.forEach(unit => {
      const u = unit as Unit;
      console.log(`%c ${u.emoji} ${u.name} - Health: ${u.health}/${u.maxHealth}`,
        'background: rgba(0, 0, 255, 0.2); border-radius: 3px; padding: 2px 4px;');
    });
    
    console.log(`%cRemaining Enemy units (${enemyUnits.length}):`, 'font-weight: bold; color: red;');
    enemyUnits.forEach(unit => {
      const u = unit as Unit;
      console.log(`%c ${u.emoji} ${u.name} - Health: ${u.health}/${u.maxHealth}`,
        'background: rgba(255, 0, 0, 0.2); border-radius: 3px; padding: 2px 4px;');
    });
    
    // Check for battle over
    if (playerUnits.length === 0) {
      this.gameRunning = false;
      console.log(`%c===== BATTLE OVER: ENEMY WINS! =====`, 'font-weight: bold; font-size: 16px; color: red; background: rgba(128, 0, 128, 0.1); padding: 5px;');
      this.addToActionLog("Battle over: Enemy wins!");
      
      // Remove all units to end the battle
      const allUnits = [...this.grid.getActorsInInitiativeOrder()];
      for (const unit of allUnits) {
        this.grid.removeActor(unit);
      }
    } else if (enemyUnits.length === 0) {
      this.gameRunning = false;
      console.log(`%c===== BATTLE OVER: PLAYER WINS! =====`, 'font-weight: bold; font-size: 16px; color: blue; background: rgba(128, 0, 128, 0.1); padding: 5px;');
      this.addToActionLog("Battle over: Player wins!");
      
      // Remove all units to end the battle
      const allUnits = [...this.grid.getActorsInInitiativeOrder()];
      for (const unit of allUnits) {
        this.grid.removeActor(unit);
      }
    }
    
    // Update actor list in case some died
    if (this.turnManager) {
      this.turnManager.updateActors(this.grid.getActorsInInitiativeOrder());
    }
  }

  private createUnits() {
    if (this.useRandomEncounter) {
      // Use the encounter generator for random unit placement
      const encounterGenerator = new EncounterGenerator(this.grid, this);
      encounterGenerator.generateRandomEncounter();
      
      // Initialize turn manager with actors in initiative order
      this.turnManager = new TurnManager(
        this.grid.getActorsInInitiativeOrder(),
        this.onRoundComplete
      );
      this._currentActor = this.turnManager.getCurrentActor();
      
      console.log(`%c===== BATTLE BEGINS: ROUND 1 =====`, 'font-weight: bold; font-size: 14px; color: green;');
      this.addToActionLog("Random encounter generated! Round 1 begins!");
    } else {
      // Create player units - mixed manual and automatic
      const playerUnitConfigs = [
        { type: 'merk', pos: { x: 2, y: 2 }, control: ControlType.Manual, initiative: 10 },
        { type: 'imp', pos: { x: 4, y: 2 }, control: ControlType.Automatic, initiative: 9 },
        { type: 'rogue', pos: { x: 3, y: 3 }, control: ControlType.Manual, initiative: 12 },
        { type: 'dog', pos: { x: 5, y: 3 }, control: ControlType.Automatic, initiative: 7 }
      ];
      
      for (const config of playerUnitConfigs) {
        // Make sure position is valid
        if (!this.grid?.isValidMove(config.pos.x, config.pos.y)) {
          continue;
        }
        
        const unit = UnitFactory.createUnit(
          config.type,
          config.pos.x,
          config.pos.y,
          Allegiance.Player,
          config.initiative,
          this.grid,
          config.control === ControlType.Manual // Only manual are player-controlled UI-wise
        );
        // Override the control type
        (unit as Unit).controlType = config.control;
        this.grid.addActor(unit, config.pos.x, config.pos.y);
        this.add(unit);
      }
      
      // Create enemy units - all automatic with all unit types
      const enemyUnitConfigs = [
        { type: 'merk', pos: { x: GRID_WIDTH - 3, y: 3 }, control: ControlType.Automatic, initiative: 8 },
        { type: 'imp', pos: { x: GRID_WIDTH - 5, y: 2 }, control: ControlType.Automatic, initiative: 7 },
        { type: 'rogue', pos: { x: GRID_WIDTH - 4, y: 4 }, control: ControlType.Automatic, initiative: 11 },
        { type: 'dog', pos: { x: GRID_WIDTH - 2, y: 5 }, control: ControlType.Automatic, initiative: 6 }
      ];
      
      for (const config of enemyUnitConfigs) {
        // Make sure position is valid
        if (!this.grid?.isValidMove(config.pos.x, config.pos.y)) {
          continue;
        }
        
        const unit = UnitFactory.createUnit(
          config.type,
          config.pos.x,
          config.pos.y,
          Allegiance.Enemy,
          config.initiative,
          this.grid
        );
        // Override the control type
        (unit as Unit).controlType = config.control;
        this.grid.addActor(unit, config.pos.x, config.pos.y);
        this.add(unit);
      }
      
      // Initialize turn manager with actors in initiative order
      this.turnManager = new TurnManager(
        this.grid.getActorsInInitiativeOrder(),
        this.onRoundComplete
      );
      this._currentActor = this.turnManager.getCurrentActor();
      
      console.log(`%c===== PRESET BATTLE BEGINS: ROUND 1 =====`, 'font-weight: bold; font-size: 14px; color: green;');
    }
  }

  private createUI() {
    // Add background for UI area
    const uiBackground = new ex.Actor({
      pos: new ex.Vector(GRID_WIDTH * TILE_SIZE / 2, GRID_HEIGHT * TILE_SIZE + 40),
      width: GRID_WIDTH * TILE_SIZE,
      height: 80,
      color: ex.Color.fromHex('#f8f8f8')
    });
    this.add(uiBackground);
    
    // Add turn indicator
    this.turnText = new ex.Label({
      text: 'Current Turn: Player',
      pos: new ex.Vector(10, GRID_HEIGHT * TILE_SIZE + 15),
      font: new ex.Font({
        family: 'Arial',
        size: 16,
        unit: ex.FontUnit.Px
      }),
      color: ex.Color.Black
    });
    this.add(this.turnText);
    
    // Add health indicator
    this.healthText = new ex.Label({
      text: 'Health: N/A',
      pos: new ex.Vector(GRID_WIDTH * TILE_SIZE - 100, GRID_HEIGHT * TILE_SIZE + 15),
      font: new ex.Font({
        family: 'Arial',
        size: 16,
        unit: ex.FontUnit.Px
      }),
      color: ex.Color.Black
    });
    this.add(this.healthText);
    
    // Create action log (last 3 actions)
    for (let i = 0; i < 3; i++) {
      const actionLabel = new ex.Label({
        text: '',
        pos: new ex.Vector(10, GRID_HEIGHT * TILE_SIZE + 40 + i * 20),
        font: new ex.Font({
          family: 'Arial',
          size: 14,
          unit: ex.FontUnit.Px
        }),
        color: ex.Color.fromHex('#444444')
      });
      this.add(actionLabel);
      this.actionLog.push(actionLabel);
    }
  }
  
  // Add a message to the action log
  public addToActionLog(message: string) {
    this.actionLogData.unshift(message); // Add to front
    this.actionLogData = this.actionLogData.slice(0, 3); // Keep only last 3
    this.updateActionLog();
  }
  
  // Update the action log display
  private updateActionLog() {
    for (let i = 0; i < this.actionLog.length; i++) {
      if (i < this.actionLogData.length) {
        this.actionLog[i].text = this.actionLogData[i];
      } else {
        this.actionLog[i].text = '';
      }
    }
  }
  
  private updateTurnText() {
    if (this.turnText && this._currentActor) {
      const actor = this._currentActor as Unit;
      const allegiance = actor.allegiance === Allegiance.Player ? 'Player' : 'Enemy';
      const actionPoints = actor.actionPoints;
      this.turnText.text = `Turn: ${actor.name} (${allegiance}) AP: ${actionPoints}`;
      
      if (this.healthText) {
        this.healthText.text = `HP: ${actor.health}/${actor.maxHealth}`;
        this.healthText.color = actor.allegiance === Allegiance.Player ?
          ex.Color.Blue : ex.Color.Red;
      }
    }
  }
  
  private processNextAction() {
    // Exit if game is over
    if (!this.gameRunning) {
      // Could implement a restart button or other end-of-game UI here
      return;
    }
    
    // Ensure we have the necessary components
    if (!this._currentActor || !this.turnManager) {
      return;
    }
    
    // Check if battle is over (all units of one allegiance are defeated)
    const playerUnits = this.grid.getActorsByAllegiance(Allegiance.Player);
    const enemyUnits = this.grid.getActorsByAllegiance(Allegiance.Enemy);
    
    if (playerUnits.length === 0 || enemyUnits.length === 0) {
      const winner = playerUnits.length > 0 ? "Player" : "Enemy";
      const winnerColor = winner === "Player" ? 'blue' : 'red';
      console.log(`%c===== BATTLE OVER: ${winner} WINS! =====`, `font-weight: bold; font-size: 16px; color: ${winnerColor}; background: rgba(128, 0, 128, 0.1); padding: 5px;`);
      
      // List remaining units with emojis
      const remainingUnits = playerUnits.length > 0 ? playerUnits : enemyUnits;
      console.log(`%c${remainingUnits.length} Remaining units for ${winner}:`, `font-weight: bold; color: ${winnerColor};`);
      remainingUnits.forEach(unit => {
        const u = unit as Unit;
        console.log(`%c ${u.emoji} ${u.name} - Health: ${u.health}/${u.maxHealth}`,
          `background: rgba(${winnerColor === 'blue' ? '0, 0, 255' : '255, 0, 0'}, 0.2); border-radius: 3px; padding: 2px 4px;`);
      });
      
      // End the game
      this.gameRunning = false;
      this.addToActionLog(`${winner} has won the battle!`);
      return;
    }
    
    // Console log for turn start with emoji
    const currentUnit = this._currentActor as Unit;
    const bgColor = currentUnit.allegiance === Allegiance.Player ? 'rgba(0, 0, 255, 0.1)' : 'rgba(255, 0, 0, 0.1)';
    console.log(`%c>> TURN: ${currentUnit.emoji} ${currentUnit.name} (${currentUnit.allegiance}) with ${currentUnit.actionPoints} AP <<`,
      `font-weight: bold; background: ${bgColor}; padding: 3px; border-radius: 3px;`);
    
    // Update UI
    this.updateTurnText();
    
    // Get the next action from the current actor
    const action = this._currentActor.getNextAction();
    
    if (action) {
      // Execute the action
      action.execute();
      
      // If the actor has no more action points, move to the next actor
      if (this._currentActor.actionPoints <= 0) {
        this._currentActor = this.turnManager.nextActor();
        this.updateTurnText();
      }
      
      // Continue processing after a short delay
      setTimeout(() => this.processNextAction(), 100); // Slightly increased delay for better visualization
    } else {
      // If no action (e.g., waiting for player input), check again more frequently
      // This makes the game more responsive to player input
      requestAnimationFrame(() => this.processNextAction());
    }
  }
}